import React, { useState, useEffect, useRef } from 'react';
import theme from './CommentTopBarUnitDocuments.module.scss';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { ResidentialUnitDocument } from '@/types';
import { getResidentialUnitDocuments } from '@/services/documents';
import { faFileSearch } from '@fortawesome/pro-light-svg-icons';
import { Dropdown } from '@ygtechnologies/rubus-ui';
import { CommentTopBarButton } from '@ygtechnologies/bygr-frontend-chat';
import DocumentListItemShort from '@/seller/components/documents/DocumentListItemShort';
import { Trans } from '@lingui/react/macro';

interface CommentTopBarUnitDocumentsProps {
    residentialUnitId: number;
}

const CommentTopBarUnitDocuments: React.FC<CommentTopBarUnitDocumentsProps> = ({ residentialUnitId }) => {
    const [documents, setDocuments] = useState<ResidentialUnitDocument[] | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
    const getRequestRef = useRef<ReturnType<typeof getResidentialUnitDocuments> | null>(null);
    useEffect(() => {
        async function loadData() {
            getRequestRef.current = getResidentialUnitDocuments(residentialUnitId);
            setDocuments(await getRequestRef.current);
            getRequestRef.current = null;
        }

        if (isOpen && !documents && !getRequestRef.current) {
            loadData();
        }

        return function () {
            getRequestRef.current = null;
        };
    }, [isOpen, residentialUnitId, documents]);

    return (
        <>
            {/* Reuse the chat's top-bar button so the icon and tooltip match the sibling buttons exactly.
                The wrapping span is the dropdown's anchor — stable across renders (no remount), so floating-ui
                keeps a live reference. tooltipContent is cleared while open so the tip doesn't overlap the dropdown. */}
            <span ref={setAnchorEl} className={theme.anchor}>
                <CommentTopBarButton
                    iconName={faFileSearch}
                    active={isOpen}
                    onClick={() => setIsOpen((v) => !v)}
                    tooltipContent={isOpen ? undefined : <Trans>Residential unit documents</Trans>}
                />
            </span>
            <Dropdown isOpen={isOpen} toggle={setIsOpen} referenceElement={anchorEl}>
                <h5 className={theme.header}>
                    <Trans>Residential unit documents</Trans>
                </h5>
                <div className={theme.container}>
                    {documents === null ? (
                        <LoadingIndicator />
                    ) : (
                        documents.map((d) => <DocumentListItemShort key={d.documentId} document={d} />)
                    )}
                </div>
            </Dropdown>
        </>
    );
};

export default CommentTopBarUnitDocuments;
